import {
  getPreparedInputEntries,
  getPreparedInputPresortedPaths,
  getPreparedInputPresortedPathsContainDirectories,
  PathStoreBuilder,
  prepareInput as prepareCanonicalInput,
  preparePaths as prepareCanonicalPaths,
  preparePresortedInput as prepareCanonicalPresortedInput,
  preparePathEntries,
} from './builder';
import {
  addPath,
  collectAncestorIds,
  findNodeId,
  getDirectoryIndex,
  listPaths,
  materializeNodePath,
  movePath,
  recomputeCountsRecursive,
  removePath,
  requireNode,
} from './canonical';
import {
  PATH_STORE_CHILD_INDEX_CHUNK_THRESHOLD_EXTERNAL,
  rebuildVisibleChildChunks,
} from './child-index';
import {
  cleanupPathStoreState,
  hasActiveCleanupBlockingLoads,
} from './cleanup';
import {
  batchEvents,
  createApplyChildPatchEvent,
  createBeginChildLoadEvent,
  createCleanupEvent,
  createCompleteChildLoadEvent,
  createFailChildLoadEvent,
  createMarkDirectoryUnloadedEvent,
  finalizeEvent,
  recordEvent,
  subscribe,
} from './events';
import { getFlattenedChildDirectoryId } from './flatten';
import { getNodeDepth, isDirectoryNode } from './internal-types';
import type { NodeId } from './internal-types';
import {
  getBenchmarkInstrumentation,
  withBenchmarkPhase,
} from './internal/benchmarkInstrumentation';
import {
  collapsePath,
  expandPath,
  getVisibleCount,
  getVisibleIndexByPath,
  getVisibleRowContext,
  getVisibleSlice,
  getVisibleTreeProjectionData as getVisibleTreeProjectionDataFromState,
  getVisibleTreeProjection as getVisibleTreeProjectionFromState,
} from './projection';
import type {
  PathStoreChildPatch,
  PathStoreCleanupOptions,
  PathStoreCleanupResult,
  PathStoreConstructorOptions,
  PathStoreDirectoryLoadState,
  PathStoreEventForType,
  PathStoreEventType,
  PathStoreLoadAttempt,
  PathStoreMoveOptions,
  PathStoreOperation,
  PathStoreOptions,
  PathStorePathInfo,
  PathStorePreparedInput,
  PathStoreRemoveOptions,
  PathStoreVisibleRow,
  PathStoreVisibleRowContext,
  PathStoreVisibleTreeProjection,
  PathStoreVisibleTreeProjectionData,
} from './public-types';
import {
  compareSegmentSortKeys,
  createSegmentSortKey,
  getSegmentSortKey,
} from './sort';
import {
  beginDirectoryLoad,
  completeDirectoryLoad,
  failDirectoryLoad,
  getDirectoryLoadState as getStoredDirectoryLoadState,
  isDirectoryExpanded,
  isDirectoryLoadAttemptCurrent,
  markDirectoryUnloadedState,
  setDirectoryExpanded,
} from './state';
import { createPathStoreState } from './state';
import type { PathStoreState } from './state';

// Initializes the common all-directories-open startup shape without rerunning
// the generic count-repair walk the constructor uses for arbitrary expansion
// overrides. This keeps the presorted first-render path from paying for a
// second full-tree pass after the builder has already finalized subtree counts.
function initializeOpenVisibleCounts(state: PathStoreState): void {
  const { directories, nodes, options, rootId, presortedDirectoryNodeIds } =
    state.snapshot;
  const flattenEmptyDirectories = options.flattenEmptyDirectories === true;

  // Iterative reverse-order walk processing directories in post-order.
  // Presorted construction assigns node IDs sequentially, so a directory's
  // descendants always have higher IDs than the directory itself. This means
  // reverse iteration finalizes every descendant before its parent.
  //
  // When the builder's presorted fast path recorded the directory IDs (the
  // common case for bulk ingest), we walk just that list in reverse and
  // skip the ~94% of iterations that would be files. Otherwise we fall back
  // to scanning the full nodes array and branching on kind per iteration.
  //
  // PathStore's constructor passes `skipSubtreeCountPass: true` to
  // builder.finish(), so subtreeNodeCount arrives un-accumulated (all 1s).
  // This walk writes both subtreeNodeCount and visibleSubtreeCount for
  // every directory; the reverse order guarantees children's counts are
  // already finalized before their parent reads them.
  // Root (nodeId === rootId === 0) is never passed to walkDirectory: the
  // fallback loop starts at nodeId >= 1, and appendPresortedFilePaths only
  // pushes newly-created directories (not the pre-existing root) into
  // presortedDirectoryNodeIds. No root check needed inside the walker.
  const walkDirectory = (nodeId: NodeId): void => {
    const currentNode = nodes[nodeId];
    if (currentNode == null || !isDirectoryNode(currentNode)) {
      return;
    }

    const currentIndex = directories.get(nodeId);
    if (currentIndex == null) {
      throw new Error(
        `Unknown directory child index for node ${String(nodeId)}`
      );
    }

    const childIds = currentIndex.childIds;
    const childCount = childIds.length;
    let totalChildSubtreeNodeCount = 0;
    let totalChildVisibleSubtreeCount = 0;
    for (let ci = 0; ci < childCount; ci++) {
      const childId = childIds[ci];
      if (childId == null) {
        continue;
      }
      const childNode = nodes[childId];
      totalChildSubtreeNodeCount += childNode.subtreeNodeCount;
      totalChildVisibleSubtreeCount += childNode.visibleSubtreeCount;
    }

    currentIndex.totalChildSubtreeNodeCount = totalChildSubtreeNodeCount;
    currentIndex.totalChildVisibleSubtreeCount = totalChildVisibleSubtreeCount;
    // Avoid the rebuildVisibleChildChunks function call for directories that
    // don't need chunk sums. The threshold matches the internal constant;
    // createPresortedDirectoryChildIndex initializes childVisibleChunkSums
    // to null already, so directories below the threshold need no write.
    if (childCount >= PATH_STORE_CHILD_INDEX_CHUNK_THRESHOLD_EXTERNAL) {
      rebuildVisibleChildChunks(nodes, currentIndex);
    }

    // The builder's backward subtree-count pass is skipped by PathStore, so
    // we populate subtreeNodeCount inline here. The reverse-order walk
    // guarantees children's subtreeNodeCount has already been written before
    // we read them.
    currentNode.subtreeNodeCount = 1 + totalChildSubtreeNodeCount;

    // Root is at nodeId 0 and is handled after the loop, so the root flag
    // never fires inside this body.
    let newVisibleSubtreeCount: number;
    if (flattenEmptyDirectories && childCount === 1) {
      // Flattened directories inherit their sole child's visible count
      // rather than contributing their own header row.
      const onlyChild = nodes[childIds[0]];
      newVisibleSubtreeCount =
        onlyChild != null && isDirectoryNode(onlyChild)
          ? totalChildVisibleSubtreeCount
          : 1 + totalChildVisibleSubtreeCount;
    } else {
      newVisibleSubtreeCount = 1 + totalChildVisibleSubtreeCount;
    }

    currentNode.visibleSubtreeCount = newVisibleSubtreeCount;
  };

  if (presortedDirectoryNodeIds != null) {
    for (let i = presortedDirectoryNodeIds.length - 1; i >= 0; i--) {
      walkDirectory(presortedDirectoryNodeIds[i]);
    }
  } else {
    for (let nodeId = nodes.length - 1; nodeId >= 1; nodeId--) {
      walkDirectory(nodeId);
    }
  }

  // Root is at id 0; the walk above skipped it so its visibleSubtreeCount
  // does not pick up the wrong "not-root" arithmetic. Process root now.

  const rootNode = nodes[rootId];
  const rootIndex = directories.get(rootId);
  if (rootNode == null || rootIndex == null) {
    return;
  }
  const rootChildIds = rootIndex.childIds;
  let rootTotalChildSubtreeNodeCount = 0;
  let rootTotalChildVisibleSubtreeCount = 0;
  for (let ci = 0; ci < rootChildIds.length; ci++) {
    const childId = rootChildIds[ci];
    if (childId == null) {
      continue;
    }
    const childNode = nodes[childId];
    rootTotalChildSubtreeNodeCount += childNode.subtreeNodeCount;
    rootTotalChildVisibleSubtreeCount += childNode.visibleSubtreeCount;
  }
  rootIndex.totalChildSubtreeNodeCount = rootTotalChildSubtreeNodeCount;
  rootIndex.totalChildVisibleSubtreeCount = rootTotalChildVisibleSubtreeCount;
  rebuildVisibleChildChunks(nodes, rootIndex);
  rootNode.subtreeNodeCount = 1 + rootTotalChildSubtreeNodeCount;
  rootNode.visibleSubtreeCount = rootTotalChildVisibleSubtreeCount;
}

function canInitializeOpenVisibleCounts(
  options: PathStoreConstructorOptions
): boolean {
  return (
    options.initialExpansion === 'open' &&
    (options.initialExpandedPaths == null ||
      options.initialExpandedPaths.length === 0)
  );
}

export class PathStore {
  readonly #state: PathStoreState;

  public constructor(options: PathStoreConstructorOptions = {}) {
    const instrumentation = getBenchmarkInstrumentation(options);
    const builder = withBenchmarkPhase(
      instrumentation,
      'store.builder.create',
      () => new PathStoreBuilder(options)
    );
    if (options.preparedInput != null) {
      const presortedPaths = getPreparedInputPresortedPaths(
        options.preparedInput
      );
      if (presortedPaths != null) {
        builder.appendPresortedPaths(
          presortedPaths,
          getPreparedInputPresortedPathsContainDirectories(
            options.preparedInput
          )
        );
      } else {
        // preparedInput is the caller's explicit fast path, so skip the
        // builder's redundant monotonic-order validation and only keep
        // duplicate checks.
        builder.appendPreparedPaths(
          getPreparedInputEntries(options.preparedInput),
          false
        );
      }
    } else {
      const inputPaths = options.paths ?? [];

      if (options.presorted === true) {
        builder.appendPaths(inputPaths);
      } else {
        builder.appendPreparedPaths(
          withBenchmarkPhase(instrumentation, 'store.preparePathEntries', () =>
            preparePathEntries(inputPaths, options)
          )
        );
      }
    }

    const snapshot = withBenchmarkPhase(
      instrumentation,
      'store.builder.finish',
      () =>
        // Either initializeOpenVisibleCounts or recomputeCountsRecursive runs
        // below, and both populate subtreeNodeCount + visibleSubtreeCount
        // from each directory's childIds. Skip the builder's own backward
        // accumulation pass to avoid doing the same work twice on large
        // presorted ingests.
        builder.finish({ skipSubtreeCountPass: true })
    );
    const useExplicitOpenExpansionFastPath = withBenchmarkPhase(
      instrumentation,
      'store.state.detectAllDirectoriesExpanded',
      () =>
        (options.initialExpansion ?? 'closed') === 'closed' &&
        builder.didMatchAllInitialExpandedPaths()
    );
    this.#state = withBenchmarkPhase(
      instrumentation,
      'store.state.create',
      () =>
        createPathStoreState(
          snapshot,
          useExplicitOpenExpansionFastPath
            ? 'open'
            : (options.initialExpansion ?? 'closed'),
          instrumentation
        )
    );
    if (useExplicitOpenExpansionFastPath) {
      this.#state.collapseNewDirectoriesByDefault = true;
    }

    const expandedDirectoryCount = useExplicitOpenExpansionFastPath
      ? this.#state.snapshot.directories.size - 1
      : withBenchmarkPhase(
          instrumentation,
          'store.state.initializeExpandedPaths',
          () => this.initializeExpandedPaths(options.initialExpandedPaths)
        );
    const canUseOpenVisibleCounts =
      useExplicitOpenExpansionFastPath ||
      canInitializeOpenVisibleCounts(options) ||
      ((options.initialExpansion ?? 'closed') === 'closed' &&
        expandedDirectoryCount === this.#state.snapshot.directories.size - 1) ||
      ((options.initialExpandedPaths?.length ?? 0) > 0 &&
        withBenchmarkPhase(
          instrumentation,
          'store.state.checkAllDirectoriesExpanded',
          () => this.hasAllDirectoriesExpanded()
        ));
    if (canUseOpenVisibleCounts) {
      withBenchmarkPhase(
        instrumentation,
        'store.state.initializeOpenVisibleCounts',
        () => initializeOpenVisibleCounts(this.#state)
      );
    } else {
      withBenchmarkPhase(instrumentation, 'store.state.recomputeCounts', () =>
        recomputeCountsRecursive(this.#state, this.#state.snapshot.rootId)
      );
    }
  }

  public static preparePaths(
    paths: readonly string[],
    options: PathStoreOptions = {}
  ): string[] {
    return prepareCanonicalPaths(paths, options);
  }

  public static prepareInput(
    paths: readonly string[],
    options: PathStoreOptions = {}
  ): PathStorePreparedInput {
    return prepareCanonicalInput(paths, options);
  }

  public static preparePresortedInput(
    paths: readonly string[]
  ): PathStorePreparedInput {
    return prepareCanonicalPresortedInput(paths);
  }

  public list(path?: string): string[] {
    return withBenchmarkPhase(this.#state.instrumentation, 'store.list', () =>
      listPaths(this.#state, path)
    );
  }

  public add(path: string): void {
    withBenchmarkPhase(this.#state.instrumentation, 'store.add', () => {
      const previousVisibleCount = getVisibleCount(this.#state);
      recordEvent(
        this.#state,
        finalizeEvent(
          this.#state,
          previousVisibleCount,
          addPath(this.#state, path)
        )
      );
    });
  }

  public remove(path: string, options: PathStoreRemoveOptions = {}): void {
    withBenchmarkPhase(this.#state.instrumentation, 'store.remove', () => {
      const previousVisibleCount = getVisibleCount(this.#state);
      recordEvent(
        this.#state,
        finalizeEvent(
          this.#state,
          previousVisibleCount,
          removePath(this.#state, path, options)
        )
      );
    });
  }

  public move(
    fromPath: string,
    toPath: string,
    options: PathStoreMoveOptions = {}
  ): void {
    withBenchmarkPhase(this.#state.instrumentation, 'store.move', () => {
      const previousVisibleCount = getVisibleCount(this.#state);
      const event = movePath(this.#state, fromPath, toPath, options);
      if (event != null) {
        recordEvent(
          this.#state,
          finalizeEvent(this.#state, previousVisibleCount, event)
        );
      }
    });
  }

  public batch(
    operations: readonly PathStoreOperation[] | ((store: PathStore) => void)
  ): void {
    batchEvents(this.#state, () => {
      if (typeof operations === 'function') {
        operations(this);
        return;
      }

      for (const operation of operations) {
        switch (operation.type) {
          case 'add':
            this.add(operation.path);
            break;
          case 'remove':
            this.remove(operation.path, { recursive: operation.recursive });
            break;
          case 'move':
            this.move(operation.from, operation.to, {
              collision: operation.collision,
            });
            break;
        }
      }
    });
  }

  public getVisibleCount(): number {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.getVisibleCount',
      () => getVisibleCount(this.#state)
    );
  }

  public getVisibleSlice(
    start: number,
    end: number
  ): readonly PathStoreVisibleRow[] {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.getVisibleSlice',
      () => getVisibleSlice(this.#state, start, end)
    );
  }

  public getVisibleRowContext(
    index: number
  ): PathStoreVisibleRowContext | null {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.getVisibleRowContext',
      () => getVisibleRowContext(this.#state, index)
    );
  }

  public getVisibleTreeProjection(): PathStoreVisibleTreeProjection {
    return getVisibleTreeProjectionFromState(this.#state);
  }

  public getVisibleTreeProjectionData(
    maxRows?: number
  ): PathStoreVisibleTreeProjectionData {
    return getVisibleTreeProjectionDataFromState(this.#state, maxRows);
  }

  /**
   * Resolves a path to its visible row index without building a full projection
   * index. Returns null when the path is unknown or currently hidden.
   */
  public getVisibleIndex(path: string): number | null {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.getVisibleIndex',
      () => getVisibleIndexByPath(this.#state, path)
    );
  }

  /**
   * Resolves a lookup path to the store's canonical path and item kind.
   * Lets tree adapters answer path-first queries without building a second
   * whole-tree metadata index alongside the store.
   */
  public getPathInfo(path: string): PathStorePathInfo | null {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.getPathInfo',
      () => {
        const nodeId = findNodeId(this.#state, path);
        if (nodeId == null) {
          return null;
        }

        const node = requireNode(this.#state, nodeId);
        return {
          depth: getNodeDepth(node),
          kind: isDirectoryNode(node) ? 'directory' : 'file',
          path: materializeNodePath(this.#state, nodeId),
        } satisfies PathStorePathInfo;
      }
    );
  }

  public isExpanded(path: string): boolean {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.isExpanded',
      () => {
        const directoryNodeId = this.requireDirectoryNodeId(path);
        const directoryNode = requireNode(this.#state, directoryNodeId);
        return isDirectoryExpanded(this.#state, directoryNodeId, directoryNode);
      }
    );
  }

  public expand(path: string): void {
    withBenchmarkPhase(this.#state.instrumentation, 'store.expand', () => {
      const previousVisibleCount = getVisibleCount(this.#state);
      const event = expandPath(this.#state, path);
      if (event != null) {
        recordEvent(
          this.#state,
          finalizeEvent(this.#state, previousVisibleCount, event)
        );
      }
    });
  }

  public collapse(path: string): void {
    withBenchmarkPhase(this.#state.instrumentation, 'store.collapse', () => {
      const previousVisibleCount = getVisibleCount(this.#state);
      const event = collapsePath(this.#state, path);
      if (event != null) {
        recordEvent(
          this.#state,
          finalizeEvent(this.#state, previousVisibleCount, event)
        );
      }
    });
  }

  public on<TType extends PathStoreEventType | '*'>(
    type: TType,
    handler: (event: PathStoreEventForType<TType>) => void
  ): () => void {
    return subscribe(this.#state, type, handler);
  }

  public getDirectoryLoadState(path: string): PathStoreDirectoryLoadState {
    const directoryNodeId = this.requireDirectoryNodeId(path);
    return getStoredDirectoryLoadState(this.#state, directoryNodeId);
  }

  public markDirectoryUnloaded(path: string): void {
    withBenchmarkPhase(
      this.#state.instrumentation,
      'store.markDirectoryUnloaded',
      () => {
        const directoryNodeId = this.requireDirectoryNodeId(path);
        if (
          getDirectoryIndex(this.#state, directoryNodeId).childIds.length > 0
        ) {
          throw new Error(
            `Cannot mark a directory with known children as unloaded: "${path}"`
          );
        }

        const previousVisibleCount = getVisibleCount(this.#state);
        markDirectoryUnloadedState(this.#state, directoryNodeId);
        recordEvent(
          this.#state,
          finalizeEvent(
            this.#state,
            previousVisibleCount,
            createMarkDirectoryUnloadedEvent({
              affectedAncestorIds: collectAncestorIds(
                this.#state,
                directoryNodeId
              ),
              affectedNodeIds: [directoryNodeId],
              path,
              projectionChanged:
                this.isDirectoryProjectionVisible(directoryNodeId),
            })
          )
        );
      }
    );
  }

  public beginChildLoad(path: string): PathStoreLoadAttempt {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.beginChildLoad',
      () => {
        const directoryNodeId = this.requireDirectoryNodeId(path);
        const previousVisibleCount = getVisibleCount(this.#state);
        const attempt = beginDirectoryLoad(this.#state, directoryNodeId);
        recordEvent(
          this.#state,
          finalizeEvent(
            this.#state,
            previousVisibleCount,
            createBeginChildLoadEvent({
              affectedAncestorIds: collectAncestorIds(
                this.#state,
                directoryNodeId
              ),
              affectedNodeIds: [directoryNodeId],
              attemptId: attempt.attemptId,
              path,
              projectionChanged:
                this.isDirectoryProjectionVisible(directoryNodeId),
              reused: attempt.reused,
            })
          )
        );
        return attempt;
      }
    );
  }

  public applyChildPatch(
    attempt: PathStoreLoadAttempt,
    patch: PathStoreChildPatch
  ): boolean {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.applyChildPatch',
      () => {
        const directoryNodeId = this.resolveActiveDirectoryNodeId(
          attempt.nodeId
        );
        if (
          directoryNodeId == null ||
          getStoredDirectoryLoadState(this.#state, directoryNodeId) !==
            'loading' ||
          !isDirectoryLoadAttemptCurrent(
            this.#state,
            directoryNodeId,
            attempt.attemptId
          )
        ) {
          return false;
        }

        const directoryPath = materializeNodePath(this.#state, directoryNodeId);
        this.validateChildPatch(directoryPath, patch);
        const previousVisibleCount = getVisibleCount(this.#state);
        const childEvents: import('./public-types').PathStoreSemanticEvent[] =
          [];

        for (const operation of patch.operations) {
          assertOperationTargetsDirectory(directoryPath, operation);
          const operationVisibleCount = getVisibleCount(this.#state);

          switch (operation.type) {
            case 'add':
              childEvents.push(
                finalizeEvent(
                  this.#state,
                  operationVisibleCount,
                  addPath(this.#state, operation.path)
                )
              );
              break;
            case 'remove':
              childEvents.push(
                finalizeEvent(
                  this.#state,
                  operationVisibleCount,
                  removePath(this.#state, operation.path, {
                    recursive: operation.recursive,
                  })
                )
              );
              break;
            case 'move': {
              const event = movePath(
                this.#state,
                operation.from,
                operation.to,
                { collision: operation.collision }
              );
              if (event != null) {
                childEvents.push(
                  finalizeEvent(this.#state, operationVisibleCount, event)
                );
              }
              break;
            }
          }
        }

        const projectionChanged =
          childEvents.some((event) => event.projectionChanged) ||
          this.isDirectoryProjectionVisible(directoryNodeId);

        recordEvent(
          this.#state,
          finalizeEvent(
            this.#state,
            previousVisibleCount,
            createApplyChildPatchEvent({
              affectedAncestorIds: collectAncestorIds(
                this.#state,
                directoryNodeId
              ),
              affectedNodeIds: [directoryNodeId],
              attemptId: attempt.attemptId,
              childEvents,
              path: materializeNodePath(this.#state, directoryNodeId),
              projectionChanged,
            })
          )
        );

        return true;
      }
    );
  }

  public completeChildLoad(attempt: PathStoreLoadAttempt): boolean {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.completeChildLoad',
      () => {
        const directoryNodeId = this.resolveActiveDirectoryNodeId(
          attempt.nodeId
        );
        if (directoryNodeId == null) {
          return false;
        }
        const previousVisibleCount = getVisibleCount(this.#state);
        const applied = completeDirectoryLoad(
          this.#state,
          directoryNodeId,
          attempt.attemptId
        );
        recordEvent(
          this.#state,
          finalizeEvent(
            this.#state,
            previousVisibleCount,
            createCompleteChildLoadEvent({
              affectedAncestorIds: collectAncestorIds(
                this.#state,
                directoryNodeId
              ),
              affectedNodeIds: [directoryNodeId],
              attemptId: attempt.attemptId,
              path: materializeNodePath(this.#state, directoryNodeId),
              projectionChanged:
                this.isDirectoryProjectionVisible(directoryNodeId),
              stale: !applied,
            })
          )
        );
        return applied;
      }
    );
  }

  public failChildLoad(
    attempt: PathStoreLoadAttempt,
    errorMessage?: string
  ): boolean {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.failChildLoad',
      () => {
        const directoryNodeId = this.resolveActiveDirectoryNodeId(
          attempt.nodeId
        );
        if (directoryNodeId == null) {
          return false;
        }
        const previousVisibleCount = getVisibleCount(this.#state);
        const applied = failDirectoryLoad(
          this.#state,
          directoryNodeId,
          attempt.attemptId,
          errorMessage
        );
        recordEvent(
          this.#state,
          finalizeEvent(
            this.#state,
            previousVisibleCount,
            createFailChildLoadEvent({
              affectedAncestorIds: collectAncestorIds(
                this.#state,
                directoryNodeId
              ),
              affectedNodeIds: [directoryNodeId],
              attemptId: attempt.attemptId,
              errorMessage,
              path: materializeNodePath(this.#state, directoryNodeId),
              projectionChanged:
                this.isDirectoryProjectionVisible(directoryNodeId),
              stale: !applied,
            })
          )
        );
        return applied;
      }
    );
  }

  public cleanup(
    options: PathStoreCleanupOptions = {}
  ): PathStoreCleanupResult {
    return withBenchmarkPhase(
      this.#state.instrumentation,
      'store.cleanup',
      () => {
        if (this.#state.transactionStack.length > 0) {
          throw new Error(
            'Cleanup cannot run during an open batch or transaction.'
          );
        }

        if (hasActiveCleanupBlockingLoads(this.#state)) {
          throw new Error(
            'Cleanup cannot run while directory loads are active.'
          );
        }

        const previousVisibleCount = getVisibleCount(this.#state);
        const result = cleanupPathStoreState(
          this.#state,
          options.mode ?? 'stable'
        );
        recordEvent(
          this.#state,
          finalizeEvent(
            this.#state,
            previousVisibleCount,
            createCleanupEvent({
              ...result,
              affectedAncestorIds: [],
              affectedNodeIds: [],
              projectionChanged: result.idsPreserved === false,
            })
          )
        );
        return result;
      }
    );
  }

  public getNodeCount(): number {
    return this.#state.activeNodeCount;
  }

  private initializeExpandedPaths(
    expandedPaths: readonly string[] | undefined
  ): number {
    if (expandedPaths == null || expandedPaths.length === 0) {
      return 0;
    }

    let expandedDirectoryCount = 0;
    const previousChildOffsets: number[] = [];
    const previousNodeIds: number[] = [];
    let previousEndIndex = 0;
    let previousPath: string | null = null;
    const segmentTable = this.#state.snapshot.segmentTable;
    const segmentValues = segmentTable.valueById;
    const nodes = this.#state.snapshot.nodes;
    const targetSegmentSortKeyCache = new Map<
      string,
      ReturnType<typeof createSegmentSortKey>
    >();

    for (const path of expandedPaths) {
      if (previousPath != null && path < previousPath) {
        previousPath = null;
        previousEndIndex = 0;
        previousChildOffsets.length = 0;
        previousNodeIds.length = 0;
      }

      const hasTrailingSlash =
        path.length > 0 && path.charCodeAt(path.length - 1) === 47;
      const endIndex = hasTrailingSlash ? path.length - 1 : path.length;
      if (endIndex === 0) {
        previousPath = path;
        previousEndIndex = endIndex;
        previousChildOffsets.length = 0;
        previousNodeIds.length = 0;
        continue;
      }

      let sharedDepth = 0;
      let unsharedSegmentStart = 0;
      if (previousPath != null) {
        const compareLength = Math.min(endIndex, previousEndIndex);
        let prefixMatched = true;
        for (let charIndex = 0; charIndex < compareLength; charIndex += 1) {
          const charCode = path.charCodeAt(charIndex);
          if (charCode !== previousPath.charCodeAt(charIndex)) {
            prefixMatched = false;
            break;
          }
          if (charCode === 47) {
            sharedDepth += 1;
            unsharedSegmentStart = charIndex + 1;
          }
        }

        if (prefixMatched) {
          if (
            compareLength === previousEndIndex &&
            endIndex > compareLength &&
            path.charCodeAt(compareLength) === 47
          ) {
            sharedDepth += 1;
            unsharedSegmentStart = compareLength + 1;
          } else if (
            compareLength === endIndex &&
            previousEndIndex > compareLength &&
            previousPath.charCodeAt(compareLength) === 47
          ) {
            sharedDepth += 1;
            unsharedSegmentStart = endIndex + 1;
          }
        }

        sharedDepth = Math.min(sharedDepth, previousNodeIds.length);
      }

      let currentDirectoryId =
        sharedDepth === 0
          ? this.#state.snapshot.rootId
          : (previousNodeIds[sharedDepth - 1] ?? this.#state.snapshot.rootId);
      let resolvedDepth = sharedDepth;
      let foundDirectory = true;
      let segmentStart = unsharedSegmentStart;

      while (segmentStart <= endIndex) {
        const slashIndex = path.indexOf('/', segmentStart);
        const segmentEnd =
          slashIndex === -1 || slashIndex > endIndex ? endIndex : slashIndex;
        const segment = path.slice(segmentStart, segmentEnd);
        const currentIndex = getDirectoryIndex(this.#state, currentDirectoryId);
        const childIds = currentIndex.childIds;
        const searchStartIndex =
          resolvedDepth === sharedDepth
            ? (previousChildOffsets[resolvedDepth] ?? 0)
            : 0;
        let nextChildOffset = searchStartIndex;
        let nextNodeId: number | undefined;
        const targetSegmentSortKey =
          targetSegmentSortKeyCache.get(segment) ??
          createSegmentSortKey(segment);
        targetSegmentSortKeyCache.set(segment, targetSegmentSortKey);
        const searchForSegment = (
          startIndex: number,
          endIndex: number
        ): boolean => {
          for (
            nextChildOffset = startIndex;
            nextChildOffset < endIndex;
            nextChildOffset += 1
          ) {
            const candidateNodeId = childIds[nextChildOffset];
            const candidateNode = nodes[candidateNodeId];
            const candidateSegment = segmentValues[candidateNode.nameId];
            if (candidateSegment === segment) {
              nextNodeId = candidateNodeId;
              return true;
            }
            const orderComparison = compareSegmentSortKeys(
              getSegmentSortKey(segmentTable, candidateNode.nameId),
              targetSegmentSortKey
            );
            if (
              orderComparison > 0 ||
              (orderComparison === 0 && candidateSegment > segment)
            ) {
              return false;
            }
          }
          return false;
        };

        const foundFromStart = searchForSegment(
          searchStartIndex,
          childIds.length
        );
        if (!foundFromStart && searchStartIndex > 0) {
          searchForSegment(0, searchStartIndex);
        }
        if (nextNodeId === undefined) {
          foundDirectory = false;
          break;
        }

        const nextNode = requireNode(this.#state, nextNodeId);
        if (!isDirectoryNode(nextNode)) {
          foundDirectory = false;
          break;
        }

        previousChildOffsets[resolvedDepth] = nextChildOffset;
        previousNodeIds[resolvedDepth] = nextNodeId;
        currentDirectoryId = nextNodeId;
        resolvedDepth += 1;
        if (segmentEnd === endIndex) {
          break;
        }
        segmentStart = segmentEnd + 1;
      }

      previousPath = path;
      previousEndIndex = endIndex;
      previousChildOffsets.length = resolvedDepth;
      previousNodeIds.length = resolvedDepth;
      if (!foundDirectory) {
        // A missing or non-directory lookup path must not leave behind a shared
        // prefix cache for the next entry. Otherwise the next valid path can
        // inherit an ancestor depth that was never actually expanded.
        previousPath = null;
        previousEndIndex = 0;
        previousChildOffsets.length = 0;
        previousNodeIds.length = 0;
        continue;
      }

      for (
        let depthIndex = sharedDepth;
        depthIndex < resolvedDepth;
        depthIndex += 1
      ) {
        const directoryNodeId = previousNodeIds[depthIndex];
        if (directoryNodeId == null) {
          continue;
        }

        const directoryNode = requireNode(this.#state, directoryNodeId);
        if (isDirectoryExpanded(this.#state, directoryNodeId, directoryNode)) {
          continue;
        }

        setDirectoryExpanded(this.#state, directoryNodeId, true, directoryNode);
        expandedDirectoryCount += 1;
      }
    }

    return expandedDirectoryCount;
  }

  private hasAllDirectoriesExpanded(): boolean {
    for (const directoryNodeId of this.#state.snapshot.directories.keys()) {
      if (directoryNodeId === this.#state.snapshot.rootId) {
        continue;
      }

      const directoryNode = requireNode(this.#state, directoryNodeId);
      if (!isDirectoryExpanded(this.#state, directoryNodeId, directoryNode)) {
        return false;
      }
    }

    return true;
  }

  private requireDirectoryNodeId(path: string): number {
    const directoryNodeId = findNodeId(this.#state, path);
    if (directoryNodeId == null) {
      throw new Error(`Path does not exist: "${path}"`);
    }

    const directoryNode = requireNode(this.#state, directoryNodeId);
    if (!isDirectoryNode(directoryNode)) {
      throw new Error(`Path is not a directory: "${path}"`);
    }

    return directoryNodeId;
  }

  private resolveActiveDirectoryNodeId(directoryNodeId: number): number | null {
    try {
      const directoryNode = requireNode(this.#state, directoryNodeId);
      if (!isDirectoryNode(directoryNode)) {
        throw new Error(`Node is not a directory: ${String(directoryNodeId)}`);
      }

      return directoryNodeId;
    } catch {
      return null;
    }
  }

  private isDirectoryProjectionVisible(directoryNodeId: number): boolean {
    let currentNodeId = directoryNodeId;

    while (currentNodeId !== this.#state.snapshot.rootId) {
      const currentNode = requireNode(this.#state, currentNodeId);
      const parentId = currentNode.parentId;
      if (parentId !== this.#state.snapshot.rootId) {
        const parentNode = requireNode(this.#state, parentId);
        const flattenedChildDirectoryId = getFlattenedChildDirectoryId(
          this.#state,
          parentId
        );
        if (
          !isDirectoryExpanded(this.#state, parentId, parentNode) &&
          flattenedChildDirectoryId !== currentNodeId
        ) {
          return false;
        }
      }
      currentNodeId = parentId;
    }

    return true;
  }

  private validateChildPatch(
    directoryPath: string,
    patch: PathStoreChildPatch
  ): void {
    // Validate the whole child patch against a throwaway subtree store first so
    // the real store stays atomic if any later operation would fail. This is an
    // intentionally heavier O(n) preflight for large directories and a targeted
    // optimization point if async patch workloads prove it hot.
    const validationStore = new PathStore({
      paths: this.list(directoryPath),
      presorted: true,
      sort: this.#state.snapshot.options.sort,
    });
    validationStore.batch(patch.operations);
  }
}

function assertOperationTargetsDirectory(
  directoryPath: string,
  operation: PathStoreOperation
): void {
  switch (operation.type) {
    case 'add':
    case 'remove':
      if (
        !operation.path.startsWith(directoryPath) ||
        operation.path === directoryPath
      ) {
        throw new Error(
          `Child patch operation must stay within ${directoryPath}: "${operation.path}"`
        );
      }
      break;
    case 'move':
      if (
        !operation.from.startsWith(directoryPath) ||
        !operation.to.startsWith(directoryPath) ||
        operation.from === directoryPath ||
        operation.to === directoryPath
      ) {
        throw new Error(
          `Child patch move must stay within ${directoryPath}: "${operation.from}" -> "${operation.to}"`
        );
      }
      break;
  }
}
